import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import {
  useGetCustomerMe,
  useSetCustomerPassword,
} from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/AuthLayout";

function useNextParam(): string {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const next = params.get("next");
  // Allow only same-origin path-style targets — anything starting with
  // a slash and no scheme/host. Defaults to /profile so the customer
  // ends up somewhere sensible if they arrived without a hint.
  if (next && /^\/[A-Za-z0-9\-_/]*$/.test(next)) return next;
  return "/profile";
}

export function SetPasswordPage() {
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const next = useNextParam();
  const meQ = useGetCustomerMe({ enabled: isAuthenticated });
  const mut = useSetCustomerPassword();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 32 (D104) — capture-email state. MUST be declared above
  // every conditional `return` below: the auth + meQ branches
  // resolve across multiple renders, and a useState placed after
  // them would change the hook order between renders → React
  // throws "Rendered fewer hooks than expected" and the page goes
  // blank (regression: this used to live below `meQ.data` and crashed
  // /set-password for every fresh signup).
  const [email, setEmail] = useState("");

  if (!isAuthenticated) return <Redirect to="/login" />;

  if (meQ.isLoading) {
    return (
      <AuthLayout>
        <p className="text-center text-ink-dark">Loading…</p>
      </AuthLayout>
    );
  }
  if (!meQ.data) {
    return (
      <AuthLayout title="Couldn't load your account">
        <p className="text-center text-red-600">Try again in a moment.</p>
      </AuthLayout>
    );
  }

  // D103 — when the server says passwordSet=false (the QR-join Bea
  // case), we hide the current-password field and the server skips
  // the verify branch on its end. When passwordSet=true, both sides
  // require it.
  const requiresCurrent = meQ.data.passwordSet;
  // Phase 32 (D104) — surface optional email capture only when the
  // customer doesn't already have one on file. Editing an existing
  // email goes through the verified Phase 12 change-flow on the
  // profile page, NOT through here.
  const showEmailCapture = meQ.data.emailSet === false;
  const emailTrimmed = email.trim();
  const emailLooksValid =
    emailTrimmed.length === 0 ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  // 12-char floor mirrors lib/api-zod CustomerPasswordSchema; matches
  // the same constraint used by signup + reset-complete.
  const passwordOk = newPassword.length >= 12;
  const canSubmit =
    passwordOk &&
    emailLooksValid &&
    (!requiresCurrent || currentPassword.length > 0) &&
    !mut.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!passwordOk) {
      setError("Password must be at least 12 characters.");
      return;
    }
    try {
      await mut.mutateAsync({
        ...(requiresCurrent ? { currentPassword } : {}),
        newPassword,
        // Phase 32 (D104) — only attach when the customer typed one
        // AND the server says they have no email yet. Server enforces
        // both conditions; the client guard is just a UX nicety.
        ...(showEmailCapture && emailTrimmed
          ? { email: emailTrimmed }
          : {}),
      });
      navigate(next);
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string };
      };
      if (e.apiError?.code === "CURRENT_PASSWORD_WRONG") {
        setError("Current password is wrong.");
      } else if (e.status === 422) {
        setError(e.apiError?.message ?? "Password didn't meet requirements.");
      } else {
        setError(e.apiError?.message ?? "Couldn't update your password.");
      }
    }
  };

  return (
    <AuthLayout
      title={requiresCurrent ? "Change password" : "Set up your password"}
      subtitle={
        requiresCurrent
          ? "Enter your current password, then a new one."
          : "Pick a password so you can always sign back in."
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {requiresCurrent && (
          <div>
            <label htmlFor="currentPassword" className="label">
              Current password
            </label>
            <input
              id="currentPassword"
              className="input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="input-current-password"
            />
          </div>
        )}
        <div>
          <label htmlFor="newPassword" className="label">
            {requiresCurrent ? "New password" : "Password"}
          </label>
          <div className="relative">
            <input
              id="newPassword"
              className="input pr-16"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
              data-testid="input-set-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-2 my-auto text-xs font-medium text-navy underline"
              tabIndex={-1}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="mt-1 text-xs text-ink-dark/70">
            At least 12 characters.
          </p>
        </div>
        {showEmailCapture && (
          <div>
            <label htmlFor="captureEmail" className="label">
              Email <span className="text-ink-dark/60 font-normal">(optional)</span>
            </label>
            <input
              id="captureEmail"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              data-testid="input-capture-email"
            />
            <p className="mt-1 text-xs text-ink-dark/70">
              Adding an email gives you a backup way to recover your
              account if you lose your phone.
            </p>
          </div>
        )}
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canSubmit}
          data-testid="button-save-password"
        >
          {mut.isPending
            ? "Saving…"
            : requiresCurrent
              ? "Save"
              : "Save & continue"}
        </button>
        {!requiresCurrent && (
          <button
            type="button"
            className="text-center text-sm text-ink-dark/70 underline"
            onClick={() => navigate(next)}
            data-testid="button-skip-set-password"
          >
            Skip for now
          </button>
        )}
      </form>
    </AuthLayout>
  );
}
